import { Card, CardActions, CardContent, Icon, Link, Typography } from '@mui/material'
import { styled } from '@mui/material/styles'
import React from 'react'
import { GoogleReCaptcha } from 'react-google-recaptcha-v3'
import { useNavigate } from 'react-router-dom-v5-compat'

import ExternalLink from '@/app/component/atom/external-link'
import { Form } from '@/app/component/form'
import SubmitButton from '@/app/component/form/submit-button'
import { useSignin } from '@/app/module/auth/hooks/use-signin'
import { captureError } from '@/app/service/util/error'

import { LoginFormValues, loginDefaultValues, loginFields, loginSchema } from './specs'

type Props = {}

const LoginForm: React.FC<Props> = () => {
  const navigate = useNavigate()
  const [login, { isLoading }] = useSignin()
  const [captchaDisplayed, setCaptchaDisplayed] = React.useState(false)
  const [formData, setFormData] = React.useState<LoginFormValues>({
    username: '',
    password: '',
  })

  const handleSubmit = (data: LoginFormValues) => {
    setFormData(data)
    setCaptchaDisplayed(true)
  }

  const handleCaptchaVerify = async (verifyToken: string) => {
    setCaptchaDisplayed(false)

    if (!formData) return

    const loginData = {
      ...formData,
      verifyToken,
    }

    const result = await login(loginData)

    if (!result) {
      return
    }

    if (result.totpEnabled) {
      navigate('/2fa')
    }
    // else no need to redirect if user is already logged ib
    // since auth guard will take care of that
  }

  React.useEffect(() => {
    // Show reCAPTCHA badge when component mounts
    if ((window as any).grecaptcha) {
      try {
        const badge = document.querySelector('.grecaptcha-badge')
        if (badge && badge.parentNode && badge.parentNode instanceof HTMLElement) {
          badge.parentNode.style.display = 'block'
        }
      } catch (e) {
        captureError(e)
      }
    }

    // Hide reCAPTCHA badge when component unmounts
    return () => {
      try {
        const badge = document.querySelector('.grecaptcha-badge')
        if (badge && badge.parentNode && badge.parentNode instanceof HTMLElement) {
          badge.parentNode.style.display = 'none'
        }
      } catch (e) {
        captureError(e)
      }
    }
  }, [])

  return (
    <StyledCard>
      <CardContent>
        <IconContainer>
          <StyledIcon>account_circle</StyledIcon>
        </IconContainer>
        <Form
          defaultValues={loginDefaultValues}
          fields={loginFields}
          schema={loginSchema}
          onSubmit={handleSubmit}
          renderActions={() => (
            <CardActions sx={{ flexDirection: 'column', padding: 0, gap: 2 }}>
              <SubmitButton isDirty isSubmitting={isLoading}>
                Log In
              </SubmitButton>

              <Typography variant="body2" sx={{ cursor: 'pointer' }}>
                <Link href="/request-reset-password" color="primary">
                  Forgot your password?
                </Link>
              </Typography>
              <Typography color="textSecondary" variant="caption">
                <strong>Tip:</strong> Enable two-factor authentication (2FA) for your engageSPARK account. Read more{' '}
                <ExternalLink
                  href="https://www.engagespark.com/support/engagespark-two-factor-authentication-2fa"
                  target="_blank"
                >
                  in this support article
                </ExternalLink>
                .
              </Typography>
            </CardActions>
          )}
        />
      </CardContent>

      {captchaDisplayed && <GoogleReCaptcha onVerify={handleCaptchaVerify} />}
    </StyledCard>
  )
}

const StyledCard = styled(Card)({
  maxWidth: '400px',
  margin: 'auto',
  marginTop: 'calc(60px + 5%)',
  marginBottom: '3rem',
  overflow: 'visible',
})

const IconContainer = styled(CardContent)({
  textAlign: 'center',
})

const StyledIcon = styled(Icon)(({ theme }) => ({
  fontSize: '4rem !important',
  color: theme.palette.primary.main,
}))

export default LoginForm

export type { Props as LoginFormProps }
